import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Avatar,
  Divider,
  InputAdornment,
  Grid,
  Tab,
  Tabs,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Collapse,
  Badge,
  Alert,
  Snackbar,
  Autocomplete,
  FormControl,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  MoreVert as MoreVertIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Cake as BirthdayIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Event as EventIcon,
  Merge as MergeIcon,
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationIcon,
  Smartphone as MobileIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api, { handleApiError } from '../../utils/api';

interface ContactEmail {
  type: 'work' | 'personal' | 'other';
  email: string;
  isPrimary: boolean;
}

interface ContactPhone {
  type: 'work' | 'mobile' | 'home' | 'other';
  number: string;
  isPrimary: boolean;
}

interface ContactAddress {
  type: 'work' | 'home' | 'other';
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface SocialProfile {
  platform: 'linkedin' | 'twitter' | 'facebook' | 'website' | 'other';
  url: string;
}

interface ContactActivity {
  id: string;
  type: 'email_sent' | 'email_received' | 'meeting' | 'call' | 'note_added';
  description: string;
  date: Date;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  emails: ContactEmail[];
  phones: ContactPhone[];
  company: string;
  jobTitle: string;
  department: string;
  addresses: ContactAddress[];
  birthday: string;
  notes: string;
  tags: string[];
  groups: string[];
  socialProfiles: SocialProfile[];
  starred: boolean;
  avatar?: string;
  createdAt: Date;
  updatedAt: Date;
  lastContactedAt?: Date;
  activities: ContactActivity[];
}

interface ContactGroup {
  id: string;
  name: string;
  color: string;
  count: number;
}

const defaultTags = ['VIP', 'Client', 'Partner', 'Friend', 'Family', 'Work', 'Lead', 'Vendor'];

const ContactsPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [contacts, setContacts] = useState<Contact[]>([]);

  const mapContact = (c: any): Contact => ({
    id: c._id || c.id,
    firstName: c.firstName || '',
    lastName: c.lastName || '',
    emails: c.emails || (c.email ? [{ type: 'work', email: c.email, isPrimary: true }] : []),
    phones: c.phones || (c.phoneNumbers?.length ? c.phoneNumbers.map((p: string) => ({ type: 'mobile', number: p, isPrimary: false })) : []),
    company: c.company || '',
    jobTitle: c.jobTitle || '',
    department: c.department || '',
    addresses: c.addresses || (c.address ? [{ type: 'work', ...c.address }] : []),
    birthday: c.birthday || '',
    notes: c.notes || '',
    tags: c.tags || [],
    groups: c.groups || [],
    socialProfiles: c.socialProfiles || [],
    starred: c.isFavorite || c.starred || false,
    avatar: c.avatar,
    createdAt: new Date(c.createdAt),
    updatedAt: new Date(c.updatedAt),
    lastContactedAt: c.lastEmailDate ? new Date(c.lastEmailDate) : undefined,
    activities: c.activities || [],
  });

  const fetchContacts = useCallback(async (search?: string, group?: string) => {
    try {
      const params: any = {};
      if (search) params.search = search;
      if (group && group !== 'all') params.group = group;
      const res = await api.get('/contacts', { params });
      const data = res.data?.data || res.data || [];
      setContacts(Array.isArray(data) ? data.map(mapContact) : []);
    } catch { /* ignore */ }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/contacts/groups');
      const data = res.data?.data || res.data || [];
      const apiGroups = Array.isArray(data) ? data.map((g: any) => ({
        id: g.name || g._id || g.id,
        name: g.name || g,
        color: g.color || '#1976d2',
        count: g.count || 0,
      })) : [];
      setGroups([
        { id: 'all', name: 'All Contacts', color: '#1976d2', count: 0 },
        ...apiGroups,
      ]);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchContacts();
    fetchGroups();
  }, [fetchContacts, fetchGroups]);

  // Listen for global refresh event from header
  useEffect(() => {
    const handleRefresh = () => { fetchContacts(); fetchGroups(); };
    window.addEventListener('app-refresh', handleRefresh);
    return () => window.removeEventListener('app-refresh', handleRefresh);
  }, [fetchContacts, fetchGroups]);

  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showActivities, setShowActivities] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success'
  });

  // New contact form state
  const [newContact, setNewContact] = useState<Partial<Contact>>({
    firstName: '',
    lastName: '',
    emails: [{ type: 'work', email: '', isPrimary: true }],
    phones: [{ type: 'mobile', number: '', isPrimary: true }],
    company: '',
    jobTitle: '',
    department: '',
    addresses: [],
    birthday: '',
    notes: '',
    tags: [],
    groups: [],
    socialProfiles: [],
  });

  // Group dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const handleAddContact = () => {
    setEditingContact(null);
    setNewContact({
      firstName: '',
      lastName: '',
      emails: [{ type: 'work', email: '', isPrimary: true }],
      phones: [{ type: 'mobile', number: '', isPrimary: true }],
      company: '',
      jobTitle: '',
      department: '',
      addresses: [],
      birthday: '',
      notes: '',
      tags: [],
      groups: [],
      socialProfiles: [],
    });
    setShowAdvanced(false);
    setOpenDialog(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setNewContact({
      firstName: contact.firstName,
      lastName: contact.lastName,
      emails: contact.emails.length > 0 ? contact.emails : [{ type: 'work', email: '', isPrimary: true }],
      phones: contact.phones.length > 0 ? contact.phones : [{ type: 'mobile', number: '', isPrimary: true }],
      company: contact.company,
      jobTitle: contact.jobTitle,
      department: contact.department,
      addresses: contact.addresses,
      birthday: contact.birthday,
      notes: contact.notes,
      tags: contact.tags,
      groups: contact.groups,
      socialProfiles: contact.socialProfiles,
    });
    setOpenDialog(true);
  };

  const handleSaveContact = async () => {
    if (!newContact.firstName?.trim() || !newContact.emails?.[0]?.email?.trim()) {
      setSnackbar({ open: true, message: 'First name and email are required', severity: 'error' });
      return;
    }

    const dto: any = {
      firstName: newContact.firstName,
      lastName: newContact.lastName || '',
      email: newContact.emails?.[0]?.email || '',
      phoneNumbers: newContact.phones?.filter(p => p.number.trim()).map(p => p.number) || [],
      company: newContact.company || '',
      jobTitle: newContact.jobTitle || '',
      notes: newContact.notes || '',
      groups: newContact.groups || [],
      tags: newContact.tags || [],
      birthday: newContact.birthday || undefined,
      address: newContact.addresses?.[0] ? {
        street: newContact.addresses[0].street,
        city: newContact.addresses[0].city,
        state: newContact.addresses[0].state,
        zip: newContact.addresses[0].zip,
        country: newContact.addresses[0].country,
      } : undefined,
    };

    try {
      if (editingContact) {
        const res = await api.put(`/contacts/${editingContact.id}`, dto);
        const updated = mapContact(res.data?.data || res.data);
        setContacts(contacts.map(c => c.id === editingContact.id ? updated : c));
        if (selectedContact?.id === editingContact.id) {
          setSelectedContact(updated);
        }
        setSnackbar({ open: true, message: 'Contact updated successfully', severity: 'success' });
      } else {
        const res = await api.post('/contacts', dto);
        const created = mapContact(res.data?.data || res.data);
        setContacts([created, ...contacts]);
        setSnackbar({ open: true, message: 'Contact created successfully', severity: 'success' });
      }
      setOpenDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleDeleteContact = async (id: string) => {
    try {
      await api.delete(`/contacts/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      if (selectedContact?.id === id) {
        setSelectedContact(null);
      }
      setSnackbar({ open: true, message: 'Contact deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleToggleStar = async (id: string) => {
    try {
      const res = await api.put(`/contacts/${id}/favorite`);
      const updated = mapContact(res.data?.data || res.data);
      setContacts(contacts.map(c => c.id === id ? updated : c));
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleAddEmail = () => {
    setNewContact({
      ...newContact,
      emails: [...(newContact.emails || []), { type: 'work', email: '', isPrimary: false }]
    });
  };

  const handleAddPhone = () => {
    setNewContact({
      ...newContact,
      phones: [...(newContact.phones || []), { type: 'mobile', number: '', isPrimary: false }]
    });
  };

  const handleAddAddress = () => {
    setNewContact({
      ...newContact,
      addresses: [...(newContact.addresses || []), { type: 'work', street: '', city: '', state: '', zip: '', country: '' }]
    });
  };

  const handleAddSocialProfile = () => {
    setNewContact({
      ...newContact,
      socialProfiles: [...(newContact.socialProfiles || []), { platform: 'linkedin', url: '' }]
    });
  };

  const handleExportContacts = async () => {
    try {
      const res = await api.get('/contacts/export');
      const exportedContacts = res.data?.data || res.data || [];
      const exportData = exportedContacts.map((c: any) => ({
        'First Name': c.firstName || '',
        'Last Name': c.lastName || '',
        'Email': c.email || '',
        'Phone': c.phoneNumbers?.[0] || '',
        'Company': c.company || '',
        'Job Title': c.jobTitle || '',
        'Tags': (c.tags || []).join(', '),
      }));

      const csv = [
        Object.keys(exportData[0] || {}).join(','),
        ...exportData.map((row: any) => Object.values(row).map(v => `"${v}"`).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.csv';
      a.click();
      setSnackbar({ open: true, message: 'Contacts exported successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
    setMoreMenuAnchor(null);
  };

  const handleImportContacts = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        const importedContacts: any[] = [];
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          
          const contact = {
            firstName: values[headers.indexOf('First Name')] || '',
            lastName: values[headers.indexOf('Last Name')] || '',
            email: values[headers.indexOf('Email')] || '',
            phoneNumbers: values[headers.indexOf('Phone')] ? [values[headers.indexOf('Phone')]] : [],
            company: values[headers.indexOf('Company')] || '',
            jobTitle: values[headers.indexOf('Job Title')] || '',
            tags: values[headers.indexOf('Tags')]?.split(';').map(t => t.trim()).filter(Boolean) || [],
          };
          
          if (contact.firstName || contact.email) {
            importedContacts.push(contact);
          }
        }
        
        const res = await api.post('/contacts/import', { contacts: importedContacts });
        const result = res.data?.data || res.data;
        await fetchContacts();
        setSnackbar({ open: true, message: `Imported ${result?.imported || importedContacts.length} contacts`, severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
    setMoreMenuAnchor(null);
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    
    const newGroup: ContactGroup = {
      id: newGroupName.toLowerCase().replace(/\s+/g, '-'),
      name: newGroupName,
      color: ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1'][Math.floor(Math.random() * 5)],
      count: 0,
    };
    setGroups(prev => [...prev, newGroup]);
    setNewGroupName('');
    setGroupDialogOpen(false);
    setSnackbar({ open: true, message: 'Group created', severity: 'success' });
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    setSnackbar({ open: true, message: 'Email copied to clipboard', severity: 'info' });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#f57c00', '#0288d1', '#5c6bc0', '#26a69a'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getActivityIcon = (type: ContactActivity['type']) => {
    switch (type) {
      case 'email_sent': return <SendIcon fontSize="small" color="primary" />;
      case 'email_received': return <EmailIcon fontSize="small" color="success" />;
      case 'meeting': return <EventIcon fontSize="small" color="secondary" />;
      case 'call': return <PhoneIcon fontSize="small" color="info" />;
      case 'note_added': return <NoteIcon fontSize="small" color="warning" />;
    }
  };

  const getSocialIcon = (platform: SocialProfile['platform']) => {
    switch (platform) {
      case 'linkedin': return <LinkedInIcon />;
      case 'twitter': return <TwitterIcon />;
      case 'facebook': return <FacebookIcon />;
      case 'website': return <WebsiteIcon />;
      default: return <WebsiteIcon />;
    }
  };

  // Update group counts
  const groupsWithCounts = groups.map(g => ({
    ...g,
    count: g.id === 'all' ? contacts.length : contacts.filter(c => c.groups.includes(g.id)).length
  }));

  const filteredContacts = contacts
    .filter(contact => {
      const matchesSearch = searchQuery === '' ||
        `${contact.firstName} ${contact.lastName} ${contact.emails.map(e => e.email).join(' ')} ${contact.company} ${contact.tags.join(' ')}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      const matchesTab = tabValue === 0 || (tabValue === 1 && contact.starred);
      const matchesGroup = selectedGroup === 'all' || contact.groups.includes(selectedGroup);
      return matchesSearch && matchesTab && matchesGroup;
    })
    .sort((a, b) => a.firstName.localeCompare(b.firstName));

  return (
    <Box sx={{ height: '100%', display: 'flex', bgcolor: '#f5f5f5' }}>
      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileImport}
      />

      {/* Groups Sidebar */}
      <Paper sx={{ width: 220, display: 'flex', flexDirection: 'column', borderRadius: 0, borderRight: '1px solid #e0e0e0' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
            GROUPS
          </Typography>
          <List dense disablePadding>
            {groupsWithCounts.map(group => (
              <ListItemButton
                key={group.id}
                selected={selectedGroup === group.id}
                onClick={() => setSelectedGroup(group.id)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: group.color }} />
                </ListItemIcon>
                <ListItemText primary={group.name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                <Typography variant="caption" color="text.secondary">{group.count}</Typography>
              </ListItemButton>
            ))}
          </List>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setGroupDialogOpen(true)}
            sx={{ mt: 1 }}
          >
            New Group
          </Button>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
            TAGS
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {defaultTags.map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                onClick={() => setSearchQuery(tag)}
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Contact List */}
      <Paper sx={{ width: 380, display: 'flex', flexDirection: 'column', borderRadius: 0 }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Contacts</Typography>
            <Box>
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAddContact}>
                Add
              </Button>
              <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
              >
                <MenuItem onClick={handleImportContacts}>
                  <ListItemIcon><ImportIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Import Contacts</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportContacts}>
                  <ListItemIcon><ExportIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Export Contacts</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setMoreMenuAnchor(null); }}>
                  <ListItemIcon><MergeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Find Duplicates</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts, tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Tab label={`All (${contacts.length})`} />
          <Tab label={`Starred (${contacts.filter(c => c.starred).length})`} />
        </Tabs>

        {/* Contact List */}
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {filteredContacts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No contacts found</Typography>
            </Box>
          ) : (
            filteredContacts.map((contact, index) => (
              <React.Fragment key={contact.id}>
                {index > 0 && <Divider />}
                <ListItemButton
                  selected={selectedContact?.id === contact.id}
                  onClick={() => setSelectedContact(contact)}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        contact.tags.includes('VIP') ? (
                          <Box sx={{ width: 10, height: 10, bgcolor: '#f4b400', borderRadius: '50%', border: '2px solid white' }} />
                        ) : null
                      }
                    >
                      <Avatar sx={{ bgcolor: getAvatarColor(contact.firstName) }}>
                        {getInitials(contact.firstName, contact.lastName)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {`${contact.firstName} ${contact.lastName}`}
                        {contact.company && (
                          <Typography variant="caption" color="text.secondary">
                            • {contact.company}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={contact.emails[0]?.email || ''}
                  />
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleStar(contact.id); }}>
                    {contact.starred ? <StarIcon color="warning" fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                </ListItemButton>
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      {/* Contact Details */}
      <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        {selectedContact ? (
          <Paper sx={{ p: 3, maxWidth: 700 }}>
            {/* Contact Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <IconButton size="small" sx={{ bgcolor: 'white', boxShadow: 1, p: 0.5 }}>
                    <PhotoCameraIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                }
              >
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: getAvatarColor(selectedContact.firstName),
                    fontSize: '2.5rem',
                    mr: 3,
                  }}
                >
                  {getInitials(selectedContact.firstName, selectedContact.lastName)}
                </Avatar>
              </Badge>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={600}>
                  {selectedContact.firstName} {selectedContact.lastName}
                </Typography>
                {selectedContact.jobTitle && (
                  <Typography variant="body1" color="text.secondary">
                    {selectedContact.jobTitle}
                    {selectedContact.department && ` • ${selectedContact.department}`}
                  </Typography>
                )}
                {selectedContact.company && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: 16 }} />
                    {selectedContact.company}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                  {selectedContact.tags.map(tag => (
                    <Chip key={tag} label={tag} size="small" color={tag === 'VIP' ? 'warning' : 'default'} />
                  ))}
                </Box>
              </Box>
              <Box>
                <IconButton onClick={() => handleToggleStar(selectedContact.id)}>
                  {selectedContact.starred ? <StarIcon color="warning" /> : <StarBorderIcon />}
                </IconButton>
                <IconButton onClick={() => handleEditContact(selectedContact)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteContact(selectedContact.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Quick Actions */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Button variant="contained" startIcon={<EmailIcon />} href={`mailto:${selectedContact.emails[0]?.email}`}>
                Send Email
              </Button>
              {selectedContact.phones[0] && (
                <Button variant="outlined" startIcon={<PhoneIcon />} href={`tel:${selectedContact.phones[0].number}`}>
                  Call
                </Button>
              )}
              <Button variant="outlined" startIcon={<EventIcon />}>
                Schedule Meeting
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Contact Information */}
            <Grid container spacing={3}>
              {/* Emails */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                  EMAIL ADDRESSES
                </Typography>
                {selectedContact.emails.map((email, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{email.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{email.type}</Typography>
                    </Box>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => handleCopyEmail(email.email)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {email.isPrimary && <Chip label="Primary" size="small" color="primary" sx={{ height: 20 }} />}
                  </Box>
                ))}
              </Grid>

              {/* Phones */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                  PHONE NUMBERS
                </Typography>
                {selectedContact.phones.map((phone, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {phone.type === 'mobile' ? <MobileIcon color="action" fontSize="small" /> : <PhoneIcon color="action" fontSize="small" />}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{phone.number}</Typography>
                      <Typography variant="caption" color="text.secondary">{phone.type}</Typography>
                    </Box>
                    {phone.isPrimary && <Chip label="Primary" size="small" color="primary" sx={{ height: 20 }} />}
                  </Box>
                ))}
              </Grid>

              {/* Birthday */}
              {selectedContact.birthday && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    BIRTHDAY
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BirthdayIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {format(new Date(selectedContact.birthday), 'MMMM d, yyyy')}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Addresses */}
              {selectedContact.addresses.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    ADDRESSES
                  </Typography>
                  {selectedContact.addresses.map((addr, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <LocationIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2">{addr.street}</Typography>
                        <Typography variant="body2">{addr.city}, {addr.state} {addr.zip}</Typography>
                        <Typography variant="body2">{addr.country}</Typography>
                        <Typography variant="caption" color="text.secondary">{addr.type}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Grid>
              )}

              {/* Social Profiles */}
              {selectedContact.socialProfiles.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    SOCIAL PROFILES
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedContact.socialProfiles.map((profile, idx) => (
                      <Tooltip key={idx} title={profile.platform}>
                        <IconButton
                          size="small"
                          href={profile.url}
                          target="_blank"
                          sx={{ bgcolor: 'action.hover' }}
                        >
                          {getSocialIcon(profile.platform)}
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Notes */}
              {selectedContact.notes && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    NOTES
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="body2">{selectedContact.notes}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* Activity Timeline */}
            {selectedContact.activities.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Activity History
                  </Typography>
                  <IconButton size="small" onClick={() => setShowActivities(!showActivities)}>
                    {showActivities ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={showActivities}>
                  <List dense>
                    {selectedContact.activities.map((activity) => (
                      <ListItemButton key={activity.id} sx={{ borderRadius: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getActivityIcon(activity.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.description}
                          secondary={format(activity.date, 'MMM d, yyyy h:mm a')}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* Meta Information */}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Created: {format(selectedContact.createdAt, 'MMM d, yyyy')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Updated: {format(selectedContact.updatedAt, 'MMM d, yyyy')}
              </Typography>
              {selectedContact.lastContactedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last contact: {format(selectedContact.lastContactedAt, 'MMM d, yyyy')}
                </Typography>
              )}
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <PersonIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Select a contact</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a contact from the list to view details
            </Typography>
          </Box>
        )}
      </Box>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Basic Info */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={newContact.firstName}
                onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Last Name"
                fullWidth
                value={newContact.lastName}
                onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
              />
            </Grid>

            {/* Emails */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Email Addresses</Typography>
              {newContact.emails?.map((email, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <Select
                      value={email.type}
                      onChange={(e) => {
                        const emails = [...(newContact.emails || [])];
                        emails[idx] = { ...emails[idx], type: e.target.value as ContactEmail['type'] };
                        setNewContact({ ...newContact, emails });
                      }}
                    >
                      <MenuItem value="work">Work</MenuItem>
                      <MenuItem value="personal">Personal</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Email address"
                    type="email"
                    value={email.email}
                    onChange={(e) => {
                      const emails = [...(newContact.emails || [])];
                      emails[idx] = { ...emails[idx], email: e.target.value };
                      setNewContact({ ...newContact, emails });
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const emails = newContact.emails?.filter((_, i) => i !== idx) || [];
                      setNewContact({ ...newContact, emails });
                    }}
                    disabled={newContact.emails?.length === 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddEmail}>
                Add Email
              </Button>
            </Grid>

            {/* Phones */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Phone Numbers</Typography>
              {newContact.phones?.map((phone, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <Select
                      value={phone.type}
                      onChange={(e) => {
                        const phones = [...(newContact.phones || [])];
                        phones[idx] = { ...phones[idx], type: e.target.value as ContactPhone['type'] };
                        setNewContact({ ...newContact, phones });
                      }}
                    >
                      <MenuItem value="mobile">Mobile</MenuItem>
                      <MenuItem value="work">Work</MenuItem>
                      <MenuItem value="home">Home</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Phone number"
                    value={phone.number}
                    onChange={(e) => {
                      const phones = [...(newContact.phones || [])];
                      phones[idx] = { ...phones[idx], number: e.target.value };
                      setNewContact({ ...newContact, phones });
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const phones = newContact.phones?.filter((_, i) => i !== idx) || [];
                      setNewContact({ ...newContact, phones });
                    }}
                    disabled={newContact.phones?.length === 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddPhone}>
                Add Phone
              </Button>
            </Grid>

            {/* Company Info */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Company"
                fullWidth
                value={newContact.company}
                onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Job Title"
                fullWidth
                value={newContact.jobTitle}
                onChange={(e) => setNewContact({ ...newContact, jobTitle: e.target.value })}
              />
            </Grid>

            {/* Tags */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                freeSolo
                options={defaultTags}
                value={newContact.tags || []}
                onChange={(_, newValue) => setNewContact({ ...newContact, tags: newValue })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" key={index} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Add tags" />
                )}
              />
            </Grid>

            {/* Advanced Section Toggle */}
            <Grid size={{ xs: 12 }}>
              <Button
                onClick={() => setShowAdvanced(!showAdvanced)}
                endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                color="inherit"
              >
                {showAdvanced ? 'Hide' : 'Show'} Advanced Options
              </Button>
            </Grid>

            {/* Advanced Fields */}
            <Grid size={{ xs: 12 }}>
              <Collapse in={showAdvanced}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Department"
                      fullWidth
                      value={newContact.department}
                      onChange={(e) => setNewContact({ ...newContact, department: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Birthday"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={newContact.birthday}
                      onChange={(e) => setNewContact({ ...newContact, birthday: e.target.value })}
                    />
                  </Grid>

                  {/* Addresses */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Addresses</Typography>
                    {newContact.addresses?.map((addr, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 12 }}>
                            <FormControl size="small" sx={{ width: 120, mb: 1 }}>
                              <Select
                                value={addr.type}
                                onChange={(e) => {
                                  const addresses = [...(newContact.addresses || [])];
                                  addresses[idx] = { ...addresses[idx], type: e.target.value as ContactAddress['type'] };
                                  setNewContact({ ...newContact, addresses });
                                }}
                              >
                                <MenuItem value="work">Work</MenuItem>
                                <MenuItem value="home">Home</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                              </Select>
                            </FormControl>
                            <IconButton
                              size="small"
                              sx={{ float: 'right' }}
                              onClick={() => {
                                const addresses = newContact.addresses?.filter((_, i) => i !== idx) || [];
                                setNewContact({ ...newContact, addresses });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Street address"
                              value={addr.street}
                              onChange={(e) => {
                                const addresses = [...(newContact.addresses || [])];
                                addresses[idx] = { ...addresses[idx], street: e.target.value };
                                setNewContact({ ...newContact, addresses });
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="City"
                              value={addr.city}
                              onChange={(e) => {
                                const addresses = [...(newContact.addresses || [])];
                                addresses[idx] = { ...addresses[idx], city: e.target.value };
                                setNewContact({ ...newContact, addresses });
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 3 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="State"
                              value={addr.state}
                              onChange={(e) => {
                                const addresses = [...(newContact.addresses || [])];
                                addresses[idx] = { ...addresses[idx], state: e.target.value };
                                setNewContact({ ...newContact, addresses });
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 3 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="ZIP"
                              value={addr.zip}
                              onChange={(e) => {
                                const addresses = [...(newContact.addresses || [])];
                                addresses[idx] = { ...addresses[idx], zip: e.target.value };
                                setNewContact({ ...newContact, addresses });
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Country"
                              value={addr.country}
                              onChange={(e) => {
                                const addresses = [...(newContact.addresses || [])];
                                addresses[idx] = { ...addresses[idx], country: e.target.value };
                                setNewContact({ ...newContact, addresses });
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddAddress}>
                      Add Address
                    </Button>
                  </Grid>

                  {/* Social Profiles */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Social Profiles</Typography>
                    {newContact.socialProfiles?.map((profile, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <FormControl size="small" sx={{ width: 140 }}>
                          <Select
                            value={profile.platform}
                            onChange={(e) => {
                              const socialProfiles = [...(newContact.socialProfiles || [])];
                              socialProfiles[idx] = { ...socialProfiles[idx], platform: e.target.value as SocialProfile['platform'] };
                              setNewContact({ ...newContact, socialProfiles });
                            }}
                          >
                            <MenuItem value="linkedin">LinkedIn</MenuItem>
                            <MenuItem value="twitter">Twitter</MenuItem>
                            <MenuItem value="facebook">Facebook</MenuItem>
                            <MenuItem value="website">Website</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="URL"
                          value={profile.url}
                          onChange={(e) => {
                            const socialProfiles = [...(newContact.socialProfiles || [])];
                            socialProfiles[idx] = { ...socialProfiles[idx], url: e.target.value };
                            setNewContact({ ...newContact, socialProfiles });
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const socialProfiles = newContact.socialProfiles?.filter((_, i) => i !== idx) || [];
                            setNewContact({ ...newContact, socialProfiles });
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddSocialProfile}>
                      Add Social Profile
                    </Button>
                  </Grid>

                  {/* Notes */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Notes"
                      fullWidth
                      multiline
                      rows={3}
                      value={newContact.notes}
                      onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                      placeholder="Add any additional notes about this contact..."
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveContact} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Group Name"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactsPage;
